<script setup lang="ts">
// One figure with its name. The dashboard's KPI row and its headline numbers
// (the in-range share, the area treated) are cards rather than charts: a single
// number drawn as a one-bar chart is a chart that exists to look like a chart.
// `accent` tints the card's edge when the figure carries a verdict of its own,
// so the in-range card can sit in the same palette the rate panel uses.
const props = defineProps<{
	label: string;
	value: string;
	sub?: string;
	accent?: string;
}>();
</script>

<template>
	<div
		class="stat-card"
		:style="props.accent ? { borderLeftColor: props.accent } : undefined"
	>
		<div class="stat-value">{{ value }}</div>
		<div class="stat-label">{{ label }}</div>
		<div v-if="sub" class="stat-sub">{{ sub }}</div>
	</div>
</template>

<style scoped>
.stat-card {
	display: flex;
	flex-direction: column;
	justify-content: center;
	gap: 2px;
	height: 100%;
	padding: 10px 12px;
	background: #fff;
	border: 1px solid #dee2e6;
	border-left: 3px solid #ced4da;
	border-radius: 4px;
	min-width: 0;
}

.stat-value {
	font-size: 1.35rem;
	font-weight: 600;
	line-height: 1.1;
	color: #212529;
	/* A figure is never clipped: a card too narrow for its number wraps it or
	   lets the card grow, but it never prints "12,3…". Labels below may take an
	   ellipsis; numbers may not. */
	overflow-wrap: anywhere;
}

.stat-label {
	font-size: 0.75rem;
	font-weight: 600;
	color: #495057;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.stat-sub {
	font-size: 0.7rem;
	color: #6c757d;
	/* The secondary line may run to two short lines rather than clip with an
	   ellipsis: a card that says "of treatments with a rate basis" in full is
	   worth the extra line, and a mutilated label is not. */
	line-height: 1.25;
	overflow-wrap: anywhere;
}
</style>
